import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { toast } from "react-toastify";

import { NURSE_COLORS } from "../../../nurse-dashboard/theme";
import {
  approveOccupancyApproval,
  getOccupancyApprovals,
  rejectOccupancyApproval,
} from "../../api/occupancyApprovalApi";
import {
  approveCancellationApproval,
  getCancellationApprovals,
  rejectCancellationApproval,
} from "../../api/cancellationRulesApi";
import { formatIstDateTime } from "../../../../utils/istDateTime";

function fmt(dt) {
  return formatIstDateTime(dt, "-");
}

function OccupancyApprovalsTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectNotes, setRejectNotes] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await getOccupancyApprovals({ status: "pending" }));
    } catch (err) {
      console.error("Failed to load occupancy approvals:", err);
      toast.error("Failed to load occupancy approvals.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleApprove = async (id) => {
    setBusyId(id);
    try {
      const data = await approveOccupancyApproval(id);
      if (data?.status === "Success") {
        toast.success(data.message || "Approved.");
        load();
      } else {
        toast.error(data?.message || "Could not approve.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not approve.");
    } finally {
      setBusyId(null);
    }
  };

  const handleReject = async () => {
    setBusyId(rejectTarget);
    try {
      const data = await rejectOccupancyApproval(rejectTarget, rejectNotes.trim() || undefined);
      if (data?.status === "Success") {
        toast.success(data.message || "Rejected.");
        setRejectTarget(null);
        setRejectNotes("");
        load();
      } else {
        toast.error(data?.message || "Could not reject.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not reject.");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Paper sx={{ p: 2, borderRadius: 2, border: `1px solid ${NURSE_COLORS.border}` }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Pending Occupancy Approvals
        </Typography>
        <Button startIcon={<RefreshIcon />} size="small" onClick={load} disabled={loading}>
          Refresh
        </Button>
      </Box>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: NURSE_COLORS.pageBg }}>
            <TableCell>Admission</TableCell>
            <TableCell>Bed</TableCell>
            <TableCell>Requested By</TableCell>
            <TableCell>Requested At</TableCell>
            <TableCell>Notes</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={6} align="center" sx={{ color: NURSE_COLORS.textSecondary }}>
                No pending occupancy approvals
              </TableCell>
            </TableRow>
          )}
          {rows.map((r) => (
            <TableRow key={r.id}>
              <TableCell>{r.admission_id}</TableCell>
              <TableCell>{r.bed_code}</TableCell>
              <TableCell>{r.requested_by_name || r.requested_by || "-"}</TableCell>
              <TableCell>{fmt(r.requested_at)}</TableCell>
              <TableCell>{r.notes || "-"}</TableCell>
              <TableCell align="right">
                <Button size="small" disabled={busyId === r.id} onClick={() => handleApprove(r.id)}>
                  Approve
                </Button>
                <Button size="small" color="error" disabled={busyId === r.id} onClick={() => setRejectTarget(r.id)}>
                  Reject
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Dialog open={!!rejectTarget} onClose={() => setRejectTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Reject Occupancy Approval</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            size="small"
            multiline
            minRows={2}
            label="Notes (optional)"
            value={rejectNotes}
            onChange={(e) => setRejectNotes(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectTarget(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleReject}>
            Reject
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}

function CancellationApprovalsTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectNotes, setRejectNotes] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await getCancellationApprovals({ status: "pending" }));
    } catch (err) {
      console.error("Failed to load cancellation approvals:", err);
      toast.error("Failed to load cancellation approvals.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleApprove = async (id) => {
    setBusyId(id);
    try {
      const data = await approveCancellationApproval(id);
      if (data?.status === "Success") {
        toast.success(data.message || "Cancellation approved.");
        load();
      } else {
        toast.error(data?.message || "Could not approve.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not approve.");
    } finally {
      setBusyId(null);
    }
  };

  const handleReject = async () => {
    setBusyId(rejectTarget);
    try {
      const data = await rejectCancellationApproval(rejectTarget, rejectNotes.trim() || undefined);
      if (data?.status === "Success") {
        toast.success(data.message || "Rejected.");
        setRejectTarget(null);
        setRejectNotes("");
        load();
      } else {
        toast.error(data?.message || "Could not reject.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not reject.");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Paper sx={{ p: 2, borderRadius: 2, border: `1px solid ${NURSE_COLORS.border}` }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Pending Cancellation Approvals
        </Typography>
        <Button startIcon={<RefreshIcon />} size="small" onClick={load} disabled={loading}>
          Refresh
        </Button>
      </Box>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: NURSE_COLORS.pageBg }}>
            <TableCell>Admission</TableCell>
            <TableCell>Reason</TableCell>
            <TableCell>Requested By</TableCell>
            <TableCell>Requested At</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={5} align="center" sx={{ color: NURSE_COLORS.textSecondary }}>
                No pending cancellation approvals
              </TableCell>
            </TableRow>
          )}
          {rows.map((r) => (
            <TableRow key={r.id}>
              <TableCell>{r.admission_id}</TableCell>
              <TableCell>{r.reason_text}</TableCell>
              <TableCell>{r.requested_by_name || r.requested_by || "-"}</TableCell>
              <TableCell>{fmt(r.requested_at)}</TableCell>
              <TableCell align="right">
                <Button size="small" disabled={busyId === r.id} onClick={() => handleApprove(r.id)}>
                  Approve
                </Button>
                <Button size="small" color="error" disabled={busyId === r.id} onClick={() => setRejectTarget(r.id)}>
                  Reject
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Dialog open={!!rejectTarget} onClose={() => setRejectTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Reject Cancellation Request</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            size="small"
            multiline
            minRows={2}
            label="Notes (optional)"
            value={rejectNotes}
            onChange={(e) => setRejectNotes(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectTarget(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleReject}>
            Reject
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}

// Unified approvals inbox for G11 Part B (Bed Occupancy Approvals) and G13
// (Cancellation Approvals) — both are supervisor "approve or reject a
// pending request" workflows, so they share one page with tabs.
export default function ApprovalsCenter() {
  const [tab, setTab] = useState(0);

  return (
    <Box sx={{ bgcolor: NURSE_COLORS.pageBg, minHeight: "100vh", p: 3 }}>
      <Typography variant="h6" color={NURSE_COLORS.textPrimary} sx={{ mb: 2 }}>
        Approvals Center
      </Typography>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Occupancy Approvals" />
        <Tab label="Cancellation Approvals" />
      </Tabs>
      {tab === 0 ? <OccupancyApprovalsTab /> : <CancellationApprovalsTab />}
    </Box>
  );
}
